import { useEffect, useState } from 'react';
import { CURRENT_LOCATION_ID } from '../util/googleMaps';
import { useGeocoder } from '../services/GeocoderGoogleMaps';
import { getCurrentEntityLocationCache, setCurrentEntityLocationCache } from '../util/localStorage';

const currentLocationData = {
    id: CURRENT_LOCATION_ID,
    predictionPlace: {},
};

const pendingRequests = Object.create(null);

const normalizeLocationData = value => {
    // Consumers expect location data to always be an object.
    if (value && typeof value === 'object') {
        return value;
    }

    return {};
};

const getPlaceDetails = (geocoder, cacheKey) => {
    const cached = getCurrentEntityLocationCache();

    if (cached) {
        return Promise.resolve(cached);
    }

    // Reuse an in-progress request for the same location.
    if (pendingRequests[cacheKey]) {
        return pendingRequests[cacheKey];
    }

    const request = geocoder
        .getPlaceDetails(currentLocationData)
        .then(value => {
            const locationData = normalizeLocationData(value);

            setCurrentEntityLocationCache(locationData);

            return locationData;
        })
        .finally(() => {
            delete pendingRequests[cacheKey];
        });

    pendingRequests[cacheKey] = request;

    return request;
};

/**
 * Resolves the current entity's location.
 *
 * Supplied coordinates are returned directly. If coordinates are unavailable,
 * the browser's current location is resolved through Google Geocoding and
 * cached for 30 days (see CURRENT_ENTITY_LOCATION_CACHE_TTL).
 *
 * @param {Object} [options]
 * @param {{lat: number|string, lng: number|string}} [options.location]
 * Entity location coordinates.
 * @param {boolean} [options.allowed=true]
 * Whether location resolution is allowed.
 * @returns {Object}
 * Location data containing properties such as `origin`, `address`, and `bounds`.
 */
export const useCurrentEntityLocation = ({ location, allowed = true } = {}) => {
    const [currentEntityLocationData, setCurrentEntityLocationData] = useState({});

    const geocoder = useGeocoder();
    const { lat, lng } = location || {};

    useEffect(() => {
        // Do not request location data when it is not needed.
        if (!allowed) return;

        let isActive = true;

        // Use supplied coordinates without making a geocoding request.
        if (lat != null && lng != null) {
            setCurrentEntityLocationData({ origin: { lat, lng } });
        } else {
            getPlaceDetails(geocoder, 'current').then(response => {
                // Ignore responses after unmount or dependency changes.
                if (isActive) {
                    setCurrentEntityLocationData(normalizeLocationData(response));
                }
            });
        }

        return () => {
            isActive = false;
        };
    }, [lat, lng, allowed, geocoder]);

    return normalizeLocationData(currentEntityLocationData);
};
